import PropTypes from 'prop-types';
import { useEffect, useReducer } from 'react';
import * as Sentry from '@sentry/react';
import jwtDecode from 'jwt-decode';
// utils
import { isEmpty } from 'lodash';
import { getParsedData, localStorageGetItem, localStorageRemoveItem } from '../utils/storage-available';
import axios from '../utils/axios';
import { callApi } from '../utils/apiCall';
import { isValidToken, setSession, onPeerLogout } from '../utils/jwt';
import { captureAuthSessionCleared } from '../utils/authSessionTelemetry';
import { refreshAccessToken } from '../utils/authRefresh';
import { dispatch as reduxDispatch } from '../redux/store';
import { createTikTokSessionKey, resetTikTokSession } from '../redux/slices/tiktokIntegration';
import { updatePracticeId } from '../redux/slices/practiceTreatmentList';
import { getLastPractice, forgetLastPractice, userKeyOf } from '../utils/lastPractice';
import { setReminderData, setToDoData } from '../redux/slices/toDoNotifications';
import { setEmailNotifications, setWpNotifications } from '../redux/slices/liveNotificationSlice';
import { fetchMyPermissions, clearPermissions, invalidatePermissionsRequests } from '../redux/slices/permissions';
import { resetScopedAdmin } from '../redux/slices/scopedAdminUsers';
import { resetLeadPins } from '../redux/slices/leadPins';
import { classifyPermissionsResult } from './authPermissionsResult';
import { clearAllDrafts } from '../utils/callScriptDraftDB';
import { clearAllRecentFeedbackSends } from '../utils/recentFeedbackSends';
import { runBeforeLogout } from '../utils/beforeLogout';
import { AuthContext } from './AuthContext';

// ----------------------------------------------------------------------

const AUTH_INIT_RELOAD_KEY = 'auth_init_reload';
const AUTH_INIT_MAX_RETRIES = 3;
const AUTH_INIT_RETRY_BASE_MS = 750;

const initialState = {
    isAuthenticated: false,
    isInitialized: false,
    authInitError: null,
    user: null,
};

const handlers = {
    INITIALIZE: (state, action) => {
        const { isAuthenticated, user } = action.payload;
        return {
            ...state,
            isAuthenticated,
            isInitialized: true,
            authInitError: null,
            user,
        };
    },
    LOGIN: (state, action) => {
        const { user } = action.payload;

        return {
            ...state,
            isAuthenticated: true,
            authInitError: null,
            user,
        };
    },
    LOGOUT: (state) => ({
        ...state,
        isAuthenticated: false,
        authInitError: null,
        user: null,
    }),
    AUTH_INIT_TRANSIENT_FAILURE: (state, action) => ({
        ...state,
        isInitialized: false,
        authInitError: action.payload,
    }),
    REGISTER: (state, action) => {
        const { user } = action.payload;

        return {
            ...state,
            isAuthenticated: true,
            authInitError: null,
            user,
        };
    }
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

function getInvalidTokenReason(token, fallback = 'refresh_expired') {
    if (!token) return fallback;
    try {
        jwtDecode(token);
        return fallback;
    } catch {
        return 'auth_init_malformed_token';
    }
}

function isAuthInvalidStatus(status) {
    return status === 401 || status === 403;
}

function hasStoredAuthTokens() {
    return Boolean(localStorageGetItem('accessToken', null) && localStorageGetItem('refreshToken', null));
}

function clearPracticeSelection() {
    reduxDispatch(updatePracticeId(null));
    localStorageRemoveItem('practiceId');
    localStorageRemoveItem('activePracticeIds');
    localStorageRemoveItem('reportPracticeIds');
    localStorageRemoveItem('adsReportingPracticeId');
}

function getFailureStatus(errorOrResult) {
    return errorOrResult?.status
        || errorOrResult?.payload?.status
        || errorOrResult?.payload?.response?.status
        || errorOrResult?.error?.status
        || errorOrResult?.error?.response?.status
        || errorOrResult?.response?.status
        || 0;
}

function AuthProvider({ children }) {
    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        let retryTimer = null;
        let cancelled = false;

        const dispatchTransientFailure = (reason, attempt, metadata = {}) => {
            if (cancelled) return;
            const retryExhausted = attempt >= AUTH_INIT_MAX_RETRIES;
            captureAuthSessionCleared(reason, {
                ...metadata,
                attempt,
                retry_exhausted: retryExhausted,
                session_preserved: true,
            });
            dispatch({
                type: 'AUTH_INIT_TRANSIENT_FAILURE',
                payload: {
                    reason,
                    retryExhausted,
                },
            });
            if (retryExhausted) return;
            retryTimer = setTimeout(() => {
                initialize(attempt + 1);
            }, AUTH_INIT_RETRY_BASE_MS * 2 ** attempt);
        };

        const initializeUnauthenticated = () => {
            if (cancelled) return;
            reduxDispatch(resetTikTokSession());
            dispatch({ type: 'INITIALIZE', payload: { isAuthenticated: false, user: null } });
        };

        const initializeAuthenticated = (user) => {
            if (cancelled) return;
            reduxDispatch(resetTikTokSession(createTikTokSessionKey()));
            sessionStorage.removeItem(AUTH_INIT_RELOAD_KEY);
            dispatch({
                type: 'INITIALIZE',
                payload: {
                    isAuthenticated: true,
                    user,
                },
            });
        };

        const clearAuthAndInitializeUnauthenticated = (reason, telemetry = {}) => {
            // Invalidate in-flight permissions requests so none can write after
            // the session is cleared (same post-clear-write concern as logout).
            invalidatePermissionsRequests();
            setSession(null, null, { reason, ...telemetry });
            clearPracticeSelection();
            initializeUnauthenticated();
        };

        const fetchPermissionsSoftly = async (practiceId, attempt) => {
            try {
                const result = await reduxDispatch(fetchMyPermissions(practiceId));
                // Single consumer decision point (see authPermissionsResult):
                // a superseded result (an older request that resolved after a
                // newer one already succeeded — W3) never invalidates the
                // session even at 401/403; a terminal 401/403 does, carrying an
                // accurate, permissions-scoped reason (not refresh_invalid).
                const { authInvalid, status, superseded, reason } = classifyPermissionsResult(result);
                if (authInvalid) {
                    return { authInvalid: true, status, reason };
                }
                if (superseded) {
                    return { authInvalid: false, status, superseded: true };
                }
                if (status >= 500 || status === 0) {
                    captureAuthSessionCleared('auth_permissions_fetch_failed', {
                        status,
                        attempt,
                        session_preserved: true,
                    });
                }
                return { authInvalid: false, status };
            } catch (error) {
                const status = getFailureStatus(error);
                if (isAuthInvalidStatus(status)) {
                    return { authInvalid: true, status };
                }
                captureAuthSessionCleared('auth_permissions_fetch_failed', {
                    status,
                    error_name: error?.name,
                    attempt,
                    session_preserved: true,
                });
                return { authInvalid: false, status };
            }
        };

        async function initialize(attempt = 0) {
            try {
                let accessToken = localStorageGetItem('accessToken', null);
                const refreshToken = localStorageGetItem('refreshToken', null);

                if (accessToken && refreshToken && !isValidToken(refreshToken)) {
                    clearAuthAndInitializeUnauthenticated(getInvalidTokenReason(refreshToken, 'refresh_expired'));
                    return;
                }

                if (accessToken && refreshToken && isValidToken(refreshToken)) {
                    // If access token is expired but refresh is valid, proactively refresh
                    // to avoid a 401 round-trip on the /auth/user/ call
                    if (!isValidToken(accessToken)) {
                        try {
                            accessToken = await refreshAccessToken();
                        } catch (refreshError) {
                            if (refreshError?.clearSession) {
                                // Refresh token is genuinely invalid/expired. Clear every
                                // per-user practice selection so a new login on the same tab
                                // cannot inherit the previous user's scoped filters.
                                clearAuthAndInitializeUnauthenticated(refreshError?.reason || 'refresh_invalid', {
                                    status: refreshError?.status || 0,
                                    error_code: refreshError?.code,
                                });
                                return;
                            }
                            dispatchTransientFailure(refreshError?.reason || 'refresh_network_error', attempt, {
                                status: refreshError?.status || 0,
                                error_code: refreshError?.code,
                            });
                            return;
                        }
                    }
                    setSession(accessToken, localStorageGetItem('refreshToken', null));

                    const response = await callApi('/api/v1/auth/user/');
                    if (response.status === 200) {
                        const user = response.data;
                        const practiceId = localStorageGetItem('practiceId', null);
                        if (practiceId) {
                            const permissionResult = await fetchPermissionsSoftly(practiceId, attempt);
                            if (permissionResult.authInvalid) {
                                // Accurate, permissions-scoped reason (not the
                                // generic refresh_invalid) with the HTTP status
                                // preserved for telemetry.
                                clearAuthAndInitializeUnauthenticated(
                                    permissionResult.reason || 'permissions_unauthorized',
                                    { status: permissionResult.status },
                                );
                                return;
                            }
                        }
                        initializeAuthenticated(user);
                        return;
                    }
                    if (isAuthInvalidStatus(response.status)) {
                        clearAuthAndInitializeUnauthenticated('refresh_invalid', { status: response.status });
                        return;
                    }
                    dispatchTransientFailure('auth_user_fetch_failed', attempt, {
                        status: response.status || 0,
                    });
                } else {
                    if (accessToken || refreshToken) {
                        setSession(null, null, { reason: 'auth_init_malformed_token' });
                    }
                    initializeUnauthenticated();
                }
            } catch (err) {
                // Report to Sentry — this catch was previously silent, making
                // auth initialization failures invisible (corrupted token, API
                // unreachable on load, malformed response).
                Sentry.captureException(err, { tags: { feature: 'auth-initialize' } });
                captureAuthSessionCleared('auth_initialize_exception', {
                    error_name: err?.name,
                });
                if (hasStoredAuthTokens()) {
                    dispatchTransientFailure('auth_initialize_exception', attempt, {
                        error_name: err?.name,
                    });
                    return;
                }
                clearPracticeSelection();
                initializeUnauthenticated();
            }
        }

        initialize();

        return () => {
            cancelled = true;
            if (retryTimer) {
                clearTimeout(retryTimer);
            }
        };
    }, []);

    const login = async (email, password) => {
        reduxDispatch(resetTikTokSession());
        const response = await axios.post('/api/v1/auth/login/?format=json', {
            email,
            password,
        });
        // eslint-disable-next-line camelcase
        const { access_token, user, refresh_token } = response.data;
    
        setSession(access_token, refresh_token);
        reduxDispatch(resetTikTokSession(createTikTokSessionKey()));
        sessionStorage.removeItem(AUTH_INIT_RELOAD_KEY);

        const practiceId = localStorageGetItem('practiceId', null);
        if (practiceId) {
            await reduxDispatch(fetchMyPermissions(practiceId));
        } else {
            // Logout cleared the shared practiceId. Reopen the practice THIS user
            // last worked in (instead of the first alphabetically), but only if
            // they still have access to it — otherwise forget it and let the
            // practice list pick a default as before.
            const remembered = getLastPractice(userKeyOf(user));
            if (remembered) {
                const result = await reduxDispatch(fetchMyPermissions(remembered));
                if (result?.status === 200) {
                    reduxDispatch(updatePracticeId(remembered));
                } else {
                    forgetLastPractice(userKeyOf(user));
                }
            }
        }

        dispatch({
            type: 'LOGIN',
            payload: {
                user,
            },
        });
    };

    const register = async (data) => {
        await axios.post('/api/v1/register/', data);
    };

    const confirmemail = async (key, emailVerification) => {
        try {
            const response = await axios.post('/api/v1/register/account-confirm-email/', key);
            if(response.status === 200) {
                emailVerification({ message: 'Email confirm successfully' });
            }
        } catch (error) {
            emailVerification({ error: error.detail });
        }
    };

    const updateCurrentUser = async (user) => {
        dispatch({
            type: 'INITIALIZE',
            payload: {
                isAuthenticated: true,
                user,
            },
        });
    };

    // Everything logout() does LOCALLY (no network call): React auth state,
    // practice selection, Redux notifications/permissions/scoped-admin,
    // timers, and drafts. Factored out of logout() so a peer tab's logout
    // (delivered via the onPeerLogout subscription below) runs the EXACT
    // same teardown instead of a hand-rolled subset that can drift from it.
    // setSession(null, null, ...) itself is NOT included here — the manual
    // logout() call site below calls it directly (it owns the network-free
    // token clear + telemetry reason), while a peer logout has already had
    // its tokens cleared by jwt.js's storage listener before this runs.
    const performLocalLogout = () => {
        reduxDispatch(resetTikTokSession());
        reduxDispatch(updatePracticeId(null));
        localStorageRemoveItem('practiceId');
        reduxDispatch(setToDoData([]));
        reduxDispatch(setEmailNotifications([]));
        reduxDispatch(setWpNotifications([]));
        reduxDispatch(setReminderData([]));
        // Invalidate any in-flight fetchMyPermissions BEFORE clearing, so a
        // permissions response dispatched while still authenticated cannot
        // resolve after logout and re-populate the store (W3 post-logout write).
        invalidatePermissionsRequests();
        reduxDispatch(clearPermissions());
        // Same rationale for the scoped-admin surface: reset its state AND bump
        // its module-level request/probe generations BEFORE the next account so
        // a late prior-session response is dropped and no rows/capabilities/nav
        // determination bleed across the same-tab user switch (F1).
        reduxDispatch(resetScopedAdmin());
        // Pinned leads belong to the outgoing user's practice session. Also bumps
        // the request generation so a late in-flight list response is dropped.
        reduxDispatch(resetLeadPins());
        const intervalId = localStorageGetItem('intervalId');
        const timeoutId = localStorageGetItem('timeoutId');
        const timeoutIdV2 = localStorageGetItem('intervalIdV2');
        const snoozedData = getParsedData('timeout_ids');
        if (!isEmpty(snoozedData)) {
            snoozedData?.forEach((element) => {
                clearTimeout(element);
            });
        }
        clearInterval(intervalId);
        clearInterval(timeoutIdV2);
        clearTimeout(timeoutId);
        localStorageRemoveItem('intervalStartTime');
        localStorageRemoveItem('intervalId');
        localStorageRemoveItem('timeoutId');
        localStorageRemoveItem('intervalIdV2');
        localStorageRemoveItem('reminderData');
        localStorageRemoveItem('timeout_ids');
        // Clear call script drafts from IndexedDB on logout (data compliance)
        clearAllDrafts();
        // Clear Patient Feedback recent-send sessionStorage so a subsequent
        // user on the same tab doesn't see the prior user's send activity.
        clearAllRecentFeedbackSends();
        // FCM registration fingerprint clearing now lives centrally in
        // setSession() (src/utils/jwt.js) — every path that reaches
        // performLocalLogout() has already called setSession(null, null, ...)
        // beforehand (logout() directly; a peer-tab logout via jwt.js's
        // storage handler before emitPeerLogout fires), so a redundant call
        // here was removed.
        // The static-lookup TTL cache (enums/mergetags/globaltablecolumnpreference/
        // get_context) is now cleared centrally inside setSession() (src/utils/jwt.js)
        // whenever a session is torn down — both call sites that reach
        // performLocalLogout() (the manual logout() below, and a peer tab's
        // logout via onPeerLogout) have already called setSession(null, null, ...)
        // by the time this runs, so an explicit clearAllStaticLookupCache() call
        // here would be redundant.
        dispatch({ type: 'LOGOUT' });
    };

    const logout = async () => {
        // Server-side clean-up that needs the session (e.g. releasing My desk), bounded.
        await runBeforeLogout();
        setSession(null, null, { reason: 'manual_logout' });
        performLocalLogout();
    };

    // A sibling tab's logout (or an unverifiable cross-tab identity change)
    // is delivered here via jwt.js's storage-event → onPeerLogout pub/sub.
    // jwt.js has already cleared tokens/axios-header/timers by the time this
    // fires; running performLocalLogout() finishes the SAME teardown logout()
    // does, with no network call and no hard navigation from this listener —
    // AuthGuard redirects once isAuthenticated flips to false.
    useEffect(() => {
        const unsubscribe = onPeerLogout(() => {
            performLocalLogout();
        });
        return unsubscribe;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <AuthContext.Provider
            value={{
                ...state,
                method: 'jwt',
                login,
                logout,
                register,
                confirmemail,
                updateCurrentUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

AuthProvider.propTypes = {
    children: PropTypes.node,
};

export { AuthProvider };
